import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";

const STATUS_OPTIONS = ["new", "reviewing", "done", "wontfix"];

export default function ReviewSuggestions() {
  const [token, setToken] = useState();
  const [suggestions, setSuggestions] = useState([]);
  const [accountsById, setAccountsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const fetchedToken = await getTokenInternal();
      if (
        !fetchedToken ||
        !["owner", "management"].includes(fetchedToken.account.role)
      ) {
        navigate("/");
        return;
      }
      setToken(fetchedToken);
    };
    init();
  }, []);

  useEffect(() => {
    getTokenInternal().then((t) =>
      fetch(`${process.env.REACT_APP_API_HOST}/api/accounts`, {
        headers: t ? { Authorization: `Bearer ${t.access_token}` } : {},
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((accounts) => {
          const byId = {};
          accounts.forEach((account) => {
            byId[account.id] = account.username;
          });
          setAccountsById(byId);
        })
        .catch((err) => console.error("Error fetching accounts:", err.message))
    );
  }, []);

  const loadSuggestions = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/suggestions`,
        { headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      const data = response.ok ? await response.json() : [];
      setSuggestions(data);
    } catch (error) {
      console.error("Error loading suggestions:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, [token]);

  const handleStatusChange = async (suggestion, newStatus) => {
    setActionError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/suggestions/${suggestion.id}/status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );
      if (!response.ok) {
        throw new Error("Could not update status");
      }
      await loadSuggestions();
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleDelete = async (suggestion) => {
    setActionError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/suggestions/${suggestion.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (!response.ok) {
        throw new Error("Could not delete suggestion");
      }
      await loadSuggestions();
    } catch (error) {
      setActionError(error.message);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Suggestions & Feedback</h1>

        {loading ? (
          <p>Loading...</p>
        ) : suggestions.length === 0 ? (
          <p>No suggestions yet.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-left">
            <thead>
              <tr>
                <th className="pr-4">Submitted By</th>
                <th className="pr-4">Message</th>
                <th className="pr-4">Submitted At</th>
                <th className="pr-4">Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((suggestion) => (
                <tr key={suggestion.id}>
                  <td className="pr-4 py-1">
                    {accountsById[suggestion.account_id] || `#${suggestion.account_id}`}
                  </td>
                  <td className="pr-4 py-1 max-w-md">{suggestion.message}</td>
                  <td className="pr-4 py-1">
                    {new Date(suggestion.created_at).toLocaleString()}
                  </td>
                  <td className="pr-4 py-1">
                    <select
                      value={suggestion.status}
                      onChange={(e) => handleStatusChange(suggestion, e.target.value)}
                      className="p-1 border rounded text-black"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1">
                    <button
                      onClick={() => handleDelete(suggestion)}
                      className="p-1 border rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {actionError && <p className="text-red-500 mt-2">{actionError}</p>}
      </div>
    </div>
  );
}
